/**
 * AuthContext — fonte única de verdade para dados de autenticação:
 *   - accessToken (JWT principal)
 *   - refreshToken
 *   - userData (objeto normalizado com usuario, permissions, permissionsConfig)
 *   - ssoToken / ssoExp (SSO de curta duração para /tools)
 *   - baseBearer (bearer para renovar SSO)
 *
 * Princípios:
 * - Estado React é a única fonte canônica em runtime.
 * - sessionStorage é apenas persistência (downstream).
 * - tokenMirror é apenas leitura síncrona para código não-React (downstream).
 * - Mutações vêm do fluxo de auth (auth.ts / tokenManager / toolsSso) via
 *   evento global `auth:updated` com `detail` contendo as chaves alteradas.
 *   O Provider escuta e atualiza apenas o que foi enviado.
 *
 * React 18.3 — `<AuthContext.Provider value={...}>` (não a sintaxe do React 19).
 */

import {
	createContext,
	useContext,
	useState,
	useEffect,
	useMemo,
	useRef,
	type ReactNode,
} from "react";
import { _setMirror } from "../lib/auth/tokenMirror";

const KEYS = {
	ACCESS_TOKEN: "cdr:auth:accessToken",
	REFRESH_TOKEN: "cdr:auth:refreshToken",
	USER_DATA: "cdr:auth:userData",
	SSO_TOKEN: "cdr:tools_sso:token",
	SSO_EXP: "cdr:tools_sso:exp",
	BASE_BEARER: "cdr:tools_sso:base_bearer",
} as const;

const LEGACY = {
	ACCESS_TOKEN: "accessToken",
	USER_DATA: "userData",
	SSO_TOKEN: "tools_sso_token",
} as const;

type AuthContextValue = {
	accessToken: string | null;
	refreshToken: string | null;
	userData: any | null;
	ssoToken: string | null;
	ssoExp: string | null;
	baseBearer: string | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthUpdatedDetail = {
	accessToken?: string | null;
	refreshToken?: string | null;
	userData?: any | null;
	ssoToken?: string | null;
	ssoExp?: string | null;
	baseBearer?: string | null;
};

function readInitialAccessToken(): string | null {
	try {
		const stored = sessionStorage.getItem(KEYS.ACCESS_TOKEN);
		if (stored) return stored;
		// Migração legada
		const legacy = sessionStorage.getItem(LEGACY.ACCESS_TOKEN);
		if (legacy) {
			sessionStorage.setItem(KEYS.ACCESS_TOKEN, legacy);
			sessionStorage.removeItem(LEGACY.ACCESS_TOKEN);
			return legacy;
		}
	} catch {
		// silencioso
	}
	return null;
}

function readInitialRefreshToken(): string | null {
	try {
		return sessionStorage.getItem(KEYS.REFRESH_TOKEN);
	} catch {
		return null;
	}
}

function readInitialUserData(): any | null {
	try {
		const stored = sessionStorage.getItem(KEYS.USER_DATA);
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch {
				return null;
			}
		}
		// Migração legada
		const legacy = sessionStorage.getItem(LEGACY.USER_DATA);
		if (legacy) {
			sessionStorage.setItem(KEYS.USER_DATA, legacy);
			sessionStorage.removeItem(LEGACY.USER_DATA);
			try {
				return JSON.parse(legacy);
			} catch {
				return null;
			}
		}
	} catch {
		// silencioso
	}
	return null;
}

function readInitialSsoToken(): string | null {
	try {
		const stored = sessionStorage.getItem(KEYS.SSO_TOKEN);
		if (stored) return stored;
		// Migração legada
		const legacy = sessionStorage.getItem(LEGACY.SSO_TOKEN);
		if (legacy) {
			sessionStorage.setItem(KEYS.SSO_TOKEN, legacy);
			sessionStorage.removeItem(LEGACY.SSO_TOKEN);
			return legacy;
		}
	} catch {
		// silencioso
	}
	return null;
}

function readInitialSsoExp(): string | null {
	try {
		return sessionStorage.getItem(KEYS.SSO_EXP);
	} catch {
		return null;
	}
}

function readInitialBaseBearer(): string | null {
	try {
		return sessionStorage.getItem(KEYS.BASE_BEARER);
	} catch {
		return null;
	}
}

export function AuthProvider({ children }: { children: ReactNode }) {
	// Lazy init: lê de sessionStorage uma vez, com migração de chaves legadas.
	// Atualiza o mirror já no init síncrono — código não-React pode ler antes
	// do primeiro commit do React.
	const [accessToken, setAccessToken] = useState<string | null>(() => {
		const v = readInitialAccessToken();
		// 🔍 DEBUG
		console.log("🔐 [AUTH_PROVIDER] lazy init accessToken", {
			hasValue: !!v,
			length: v?.length ?? 0,
		});
		_setMirror({ accessToken: v });
		return v;
	});
	const [refreshToken, setRefreshToken] = useState<string | null>(() => {
		const v = readInitialRefreshToken();
		_setMirror({ refreshToken: v });
		return v;
	});
	const [userData, setUserData] = useState<any | null>(() => {
		const v = readInitialUserData();
		// 🔍 DEBUG
		console.log("🔐 [AUTH_PROVIDER] lazy init userData", {
			hasValue: !!v,
			hasUsuario: !!v?.data?.usuario,
		});
		_setMirror({ userData: v });
		return v;
	});
	const [ssoToken, setSsoToken] = useState<string | null>(() => {
		const v = readInitialSsoToken();
		_setMirror({ ssoToken: v });
		return v;
	});
	const [ssoExp, setSsoExp] = useState<string | null>(() => {
		const v = readInitialSsoExp();
		_setMirror({ ssoExp: v });
		return v;
	});
	const [baseBearer, setBaseBearer] = useState<string | null>(() => {
		const v = readInitialBaseBearer();
		_setMirror({ baseBearer: v });
		return v;
	});

	// Rastreia valores anteriores para distinguir TRANSIÇÕES reais
	// (valor → null = logout legítimo) do null INAUGURAL do mount.
	//
	// Por que isso importa: os efeitos deste Provider (pai) rodam DEPOIS dos
	// efeitos dos filhos. Se um filho gravar um token via tokenManager no seu
	// próprio useEffect (ex.: bootstrap SSO), o dispatch de `auth:updated`
	// acontece ANTES do listener abaixo registrar — o evento é perdido e o
	// estado React continua `null`. Nessa janela, o efeito inaugural NÃO pode
	// remover do storage nem zerar o mirror, senão apagaria um token válido
	// recém-gravado por código não-React.
	const prevAccessTokenRef = useRef<string | null>(accessToken);
	const prevRefreshTokenRef = useRef<string | null>(refreshToken);
	const prevUserDataRef = useRef<any | null>(userData);
	const prevSsoTokenRef = useRef<string | null>(ssoToken);
	const prevSsoExpRef = useRef<string | null>(ssoExp);
	const prevBaseBearerRef = useRef<string | null>(baseBearer);

	// Persistência: sessionStorage + mirror para accessToken.
	useEffect(() => {
		try {
			if (accessToken) {
				sessionStorage.setItem(KEYS.ACCESS_TOKEN, accessToken);
			} else if (prevAccessTokenRef.current) {
				// Transição real valor → null (logout legítimo): remover.
				// console.debug: é fluxo esperado em todo logout — o stack fica
				// disponível para diagnóstico sem poluir o console como erro.
				console.debug("🔥 [AUTH_CONTEXT] accessToken TRANSITION value → null", {
					hadValue: !!prevAccessTokenRef.current,
					previousLength: prevAccessTokenRef.current?.length ?? 0,
					stack: new Error("transition accessToken → null").stack?.split("\n").slice(0, 15).join("\n"),
				});
				sessionStorage.removeItem(KEYS.ACCESS_TOKEN);
			} else {
				// Null inaugural: NUNCA remover. Um token pode ter chegado na
				// janela pré-listener (evento perdido) — re-lê o storage e, se
				// houver valor, adota-o no estado. O efeito re-roda no branch
				// truthy e persiste normalmente.
				const recovered =
					sessionStorage.getItem(KEYS.ACCESS_TOKEN) ||
					sessionStorage.getItem(LEGACY.ACCESS_TOKEN);
				if (recovered) {
					setAccessToken(recovered);
					// Não zerar o mirror nem atualizar o prevRef: o mirror já
					// foi atualizado por quem gravou o token, e o prevRef será
					// atualizado quando o efeito re-rodar com o valor adotado.
					return;
				}
				// Sem valor no storage: no-op (mirror já está null; o
				// _setMirror abaixo é inócuo).
			}
		} catch {
			// silencioso
		}
		_setMirror({ accessToken });
		prevAccessTokenRef.current = accessToken;
	}, [accessToken]);

	// Persistência: sessionStorage + mirror para refreshToken.
	useEffect(() => {
		try {
			if (refreshToken) {
				sessionStorage.setItem(KEYS.REFRESH_TOKEN, refreshToken);
			} else if (prevRefreshTokenRef.current) {
				// Transição real valor → null (logout legítimo): remover.
				console.debug("🔥 [AUTH_CONTEXT] refreshToken TRANSITION value → null", {
					stack: new Error("transition refreshToken → null").stack?.split("\n").slice(0, 15).join("\n"),
				});
				sessionStorage.removeItem(KEYS.REFRESH_TOKEN);
			} else {
				// Null inaugural: NUNCA remover — janela do evento perdido.
				const recovered = sessionStorage.getItem(KEYS.REFRESH_TOKEN);
				if (recovered) {
					setRefreshToken(recovered);
					return;
				}
			}
		} catch {
			// silencioso
		}
		_setMirror({ refreshToken });
		prevRefreshTokenRef.current = refreshToken;
	}, [refreshToken]);

	// Persistência: sessionStorage (JSON) + mirror para userData.
	useEffect(() => {
		try {
			if (userData) {
				sessionStorage.setItem(KEYS.USER_DATA, JSON.stringify(userData));
			} else if (prevUserDataRef.current) {
				// Transição real valor → null (logout legítimo): remover.
				console.debug("🔥 [AUTH_CONTEXT] userData TRANSITION value → null", {
					hadUsuario: !!prevUserDataRef.current?.data?.usuario,
					stack: new Error("transition userData → null").stack?.split("\n").slice(0, 15).join("\n"),
				});
				sessionStorage.removeItem(KEYS.USER_DATA);
			} else {
				// Null inaugural: NUNCA remover — janela do evento perdido.
				const recoveredRaw =
					sessionStorage.getItem(KEYS.USER_DATA) ||
					sessionStorage.getItem(LEGACY.USER_DATA);
				if (recoveredRaw) {
					try {
						const recovered = JSON.parse(recoveredRaw);
						if (recovered) {
							setUserData(recovered);
							return;
						}
					} catch {
						// JSON inválido: trata como ausência de valor (no-op).
					}
				}
			}
		} catch {
			// silencioso
		}
		_setMirror({ userData });
		prevUserDataRef.current = userData;
	}, [userData]);

	// Persistência: sessionStorage + mirror para ssoToken.
	// Mesmo padrão dos campos de auth: o bootstrap SSO popula ssoToken/ssoExp/
	// baseBearer sincronamente dentro da MESMA janela pré-listener. Sem a
	// recuperação, o _setMirror do final zeraria o mirror recém-populado e o
	// estado ficaria null para sempre (evento perdido).
	useEffect(() => {
		try {
			if (ssoToken) {
				sessionStorage.setItem(KEYS.SSO_TOKEN, ssoToken);
			} else if (prevSsoTokenRef.current) {
				// Transição real valor → null (expiração/logout): remover.
				sessionStorage.removeItem(KEYS.SSO_TOKEN);
			} else {
				// Null inaugural: NUNCA remover — janela do evento perdido.
				const recovered =
					sessionStorage.getItem(KEYS.SSO_TOKEN) ||
					sessionStorage.getItem(LEGACY.SSO_TOKEN);
				if (recovered) {
					setSsoToken(recovered);
					return;
				}
			}
		} catch {
			// silencioso
		}
		_setMirror({ ssoToken });
		prevSsoTokenRef.current = ssoToken;
	}, [ssoToken]);

	// Persistência: sessionStorage + mirror para ssoExp.
	// Mesmo padrão do ssoToken — recuperação no null inaugural (só canônica).
	useEffect(() => {
		try {
			if (ssoExp) {
				sessionStorage.setItem(KEYS.SSO_EXP, ssoExp);
			} else if (prevSsoExpRef.current) {
				sessionStorage.removeItem(KEYS.SSO_EXP);
			} else {
				const recovered = sessionStorage.getItem(KEYS.SSO_EXP);
				if (recovered) {
					setSsoExp(recovered);
					return;
				}
			}
		} catch {
			// silencioso
		}
		_setMirror({ ssoExp });
		prevSsoExpRef.current = ssoExp;
	}, [ssoExp]);

	// Persistência: sessionStorage + mirror para baseBearer.
	// Mesmo padrão do ssoToken — recuperação no null inaugural (só canônica).
	useEffect(() => {
		try {
			if (baseBearer) {
				sessionStorage.setItem(KEYS.BASE_BEARER, baseBearer);
			} else if (prevBaseBearerRef.current) {
				sessionStorage.removeItem(KEYS.BASE_BEARER);
			} else {
				const recovered = sessionStorage.getItem(KEYS.BASE_BEARER);
				if (recovered) {
					setBaseBearer(recovered);
					return;
				}
			}
		} catch {
			// silencioso
		}
		_setMirror({ baseBearer });
		prevBaseBearerRef.current = baseBearer;
	}, [baseBearer]);

	// Escuta o evento global de mudança de auth.
	// auth.ts/tokenManager/toolsSso dispatcham `auth:updated` com detail contendo
	// as chaves alteradas (parcial). Atualizamos apenas o que veio.
	useEffect(() => {
		const handler = (event: Event) => {
			const detail = (event as CustomEvent<AuthUpdatedDetail>).detail;
			// 🔍 DEBUG: log de TODOS os eventos auth:updated recebidos
			console.log("🔔 [AUTH_PROVIDER] auth:updated event received", {
				hasDetail: !!detail,
				keys: detail ? Object.keys(detail) : [],
				accessTokenIsNull: detail && "accessToken" in detail && detail.accessToken === null,
				userDataIsNull: detail && "userData" in detail && detail.userData === null,
			});
			if (!detail) return;
			// Mirror é atualizado imediatamente (síncrono) — qualquer leitura entre
			// agora e o próximo commit do React vê o novo valor.
			_setMirror(detail);
			if ("accessToken" in detail) {
				setAccessToken(detail.accessToken ?? null);
			}
			if ("refreshToken" in detail) {
				setRefreshToken(detail.refreshToken ?? null);
			}
			if ("userData" in detail) {
				setUserData(detail.userData ?? null);
			}
			if ("ssoToken" in detail) {
				setSsoToken(detail.ssoToken ?? null);
			}
			if ("ssoExp" in detail) {
				setSsoExp(detail.ssoExp ?? null);
			}
			if ("baseBearer" in detail) {
				setBaseBearer(detail.baseBearer ?? null);
			}
		};
		window.addEventListener("auth:updated", handler);
		return () => window.removeEventListener("auth:updated", handler);
	}, []);

	const value = useMemo<AuthContextValue>(
		() => ({
			accessToken,
			refreshToken,
			userData,
			ssoToken,
			ssoExp,
			baseBearer,
		}),
		[accessToken, refreshToken, userData, ssoToken, ssoExp, baseBearer],
	);

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
}

function useAuthContext(): AuthContextValue {
	const ctx = useContext(AuthContext);
	if (ctx === null) {
		throw new Error(
			"useAuthContext deve ser usado dentro de <AuthProvider>.",
		);
	}
	return ctx;
}

/**
 * Hook para ler o accessToken dentro de componentes React.
 * Lança se usado fora de <AuthProvider>.
 */
export function useAccessToken(): string | null {
	return useAuthContext().accessToken;
}

/**
 * Retorna `true` quando há um accessToken pronto no contexto.
 *
 * Use para "gatear" fetches disparados no mount: no cold-start o token pode
 * ainda não ter propagado do bootstrap/refresh para o contexto, então a
 * requisição sairia sem `Authorization` e tomaria 401. Aguarde este flag e
 * inclua-o nas dependências do `useEffect` — quando o token chegar (evento
 * `auth:updated`), o efeito re-dispara já autenticado.
 */
export function useAuthReady(): boolean {
	return !!useAuthContext().accessToken;
}

/**
 * Hook para ler o refreshToken dentro de componentes React.
 * Lança se usado fora de <AuthProvider>.
 */
export function useRefreshToken(): string | null {
	return useAuthContext().refreshToken;
}

/**
 * Hook para ler o userData normalizado dentro de componentes React.
 * Lança se usado fora de <AuthProvider>.
 */
export function useUserData(): any | null {
	return useAuthContext().userData;
}

/**
 * Hook para ler o ssoToken dentro de componentes React.
 * Lança se usado fora de <AuthProvider>.
 */
export function useSsoToken(): { token: string | null; exp: string | null } {
	const ctx = useAuthContext();
	return { token: ctx.ssoToken, exp: ctx.ssoExp };
}

/**
 * Hook para ler o baseBearer dentro de componentes React.
 * Lança se usado fora de <AuthProvider>.
 */
export function useBaseBearer(): string | null {
	return useAuthContext().baseBearer;
}
